import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Copy,
  Download,
  Plus,
  Star,
  ArrowLeft,
  Filter,
  Search,
  Grid,
  List,
  TrendingUp,
  Users,
  Clock,
  Shield,
  Zap,
  BookOpen,
  Layout,
  Settings,
  Sparkles,
  Upload,
  Eye,
  FileCheck,
  Package,
  DollarSign,
  Box,
  Image as ImageIcon,
  Cpu,
  FileSpreadsheet
} from 'lucide-react';
import Button from '../../../../components/common/Button';
import Card from '../../../../components/ui/Card';
import Input from '../../../../components/common/Input';

const ProductTemplates = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const categories = [
    { id: 'all', name: 'All Templates', count: 12 },
    { id: 'electronics', name: 'Electronics', count: 4 },
    { id: 'fashion', name: 'Fashion', count: 3 },
    { id: 'home', name: 'Home & Living', count: 2 },
    { id: 'industrial', name: 'Industrial', count: 2 },
    { id: 'custom', name: 'Custom', count: 1 }
  ];

  const templates = [
    {
      id: 1,
      name: 'Electronics Master',
      description: 'Comprehensive template for all electronic devices with specifications, warranty, and compliance details',
      category: 'electronics',
      fields: 28,
      usageCount: 245,
      rating: 4.8,
      lastUsed: '2 hours ago',
      icon: '💻',
      features: ['Warranty Tracking', 'Tech Specs', 'Compliance'],
      popularity: 'high',
      createdBy: 'System Admin'
    },
    {
      id: 2,
      name: 'Fashion Collection',
      description: 'Perfect for apparel with size charts, material specs, and seasonal collections',
      category: 'fashion',
      fields: 22,
      usageCount: 189,
      rating: 4.7,
      lastUsed: 'Yesterday',
      icon: '👕',
      features: ['Size Charts', 'Material Specs', 'Seasonal'],
      popularity: 'high',
      createdBy: 'Fashion Team'
    },
    {
      id: 3,
      name: 'Home Essentials',
      description: 'Template for home goods with dimensions, materials, and assembly instructions',
      category: 'home',
      fields: 18,
      usageCount: 134,
      rating: 4.5,
      lastUsed: '3 days ago',
      icon: '🏠',
      features: ['Dimensions', 'Materials', 'Assembly'],
      popularity: 'medium',
      createdBy: 'Home Dept'
    },
    {
      id: 4,
      name: 'Industrial Equipment',
      description: 'Detailed template for industrial machinery with specifications and safety guidelines',
      category: 'industrial',
      fields: 32,
      usageCount: 87,
      rating: 4.9,
      lastUsed: '1 week ago',
      icon: '🏭',
      features: ['Safety Guidelines', 'Spec Sheets', 'Maintenance'],
      popularity: 'medium',
      createdBy: 'Industrial Team'
    },
    {
      id: 5,
      name: 'Basic Product',
      description: 'Simple template with essential fields for quick product setup',
      category: 'basic',
      fields: 12,
      usageCount: 456,
      rating: 4.3,
      lastUsed: 'Today',
      icon: '📦',
      features: ['Quick Setup', 'Essential Fields'],
      popularity: 'high',
      createdBy: 'System'
    },
    {
      id: 6,
      name: 'Digital Products',
      description: 'Template for digital goods with download links, licenses, and system requirements',
      category: 'digital',
      fields: 20,
      usageCount: 67,
      rating: 4.6,
      lastUsed: '2 days ago',
      icon: '📱',
      features: ['Licenses', 'System Reqs', 'Downloads'],
      popularity: 'low',
      createdBy: 'Digital Team'
    },
    {
      id: 7,
      name: 'Custom Business',
      description: 'Your custom template with personalized fields and workflows',
      category: 'custom',
      fields: 15,
      usageCount: 12,
      rating: 4.0,
      lastUsed: '1 month ago',
      icon: '⚙️',
      features: ['Custom Fields', 'Workflows'],
      popularity: 'low',
      createdBy: 'You'
    },
    {
      id: 8,
      name: 'Food & Beverage',
      description: 'Template for food products with nutritional info, storage, and expiration',
      category: 'food',
      fields: 25,
      usageCount: 56,
      rating: 4.4,
      lastUsed: '4 days ago',
      icon: '🍎',
      features: ['Nutritional Info', 'Storage', 'Expiration'],
      popularity: 'medium',
      createdBy: 'Food Team'
    }
  ];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = searchQuery === '' || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getPopularityColor = (popularity) => {
    switch(popularity) {
      case 'high': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const stats = [
    { label: 'Total Templates', value: '12', icon: FileText, color: 'blue' },
    { label: 'Active Users', value: '156', icon: Users, color: 'green' },
    { label: 'Avg Rating', value: '4.6', icon: Star, color: 'yellow' },
    { label: 'Time Saved', value: '240h', icon: Clock, color: 'purple' }
  ];

  // Format INR currency
  const formatINR = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="small"
            onClick={() => navigate('/seller/sales')}
            className="hover:bg-gray-100 flex items-center justify-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2 flex-shrink-0" />
            Back to Sales
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Product Templates</h1>
            <p className="text-gray-600">Accelerate product creation with professional templates</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/seller/sales/bulk-upload')}
            className="flex items-center justify-center"
          >
            <Upload className="h-4 w-4 mr-2 flex-shrink-0" />
            Bulk Upload
          </Button>
          <Button 
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 flex items-center justify-center"
          >
            <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
        <Button
          variant="outline"
          size="small"
          onClick={() => navigate('/seller/sales/add-product')}
          className="flex items-center justify-center"
        >
          <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
          Add Single Product
        </Button>
        <Button
          variant="outline"
          size="small"
          onClick={() => navigate('/seller/sales/bulk-upload')}
          className="flex items-center justify-center"
        >
          <Upload className="h-4 w-4 mr-2 flex-shrink-0" />
          Bulk Upload
        </Button>
        <Button
          variant="outline"
          size="small"
          onClick={() => window.open('/template-guidelines', '_blank')}
          className="flex items-center justify-center"
        >
          <BookOpen className="h-4 w-4 mr-2 flex-shrink-0" />
          Guidelines
        </Button>
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftIcon={<Search className="h-5 w-5 text-gray-400 flex-shrink-0" />}
          className="flex-1 min-w-[200px]"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm text-gray-500 truncate">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 truncate">{stat.value}</p>
                </div>
                <div className={`p-2 rounded-lg flex-shrink-0 ${
                  stat.color === 'blue' ? 'bg-blue-100' :
                  stat.color === 'green' ? 'bg-green-100' :
                  stat.color === 'yellow' ? 'bg-yellow-100' :
                  'bg-purple-100'
                }`}>
                  <Icon className={`h-5 w-5 ${
                    stat.color === 'blue' ? 'text-blue-600' :
                    stat.color === 'green' ? 'text-green-600' :
                    stat.color === 'yellow' ? 'text-yellow-600' :
                    'text-purple-600'
                  }`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Filters & Search */}
      <Card>
        <div className="p-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="w-full lg:w-auto">
              <Input
                placeholder="Search templates by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<Search className="h-5 w-5 text-gray-400 flex-shrink-0" />}
                className="min-w-[300px]"
              />
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <div className="hidden sm:flex flex-wrap items-center gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    {category.name}
                    <span className="ml-1.5 text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded flex-shrink-0">
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center justify-center ${viewMode === 'grid' ? 'bg-blue-50 border-blue-200' : ''}`}
                >
                  <Grid className="h-4 w-4 flex-shrink-0" />
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setViewMode('list')}
                  className={`flex items-center justify-center ${viewMode === 'list' ? 'bg-blue-50 border-blue-200' : ''}`}
                >
                  <List className="h-4 w-4 flex-shrink-0" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Mobile Categories */}
          <div className="sm:hidden mt-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.count})
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          Showing <span className="font-medium text-gray-900">{filteredTemplates.length}</span> templates
          {searchQuery && ` for "${searchQuery}"`}
        </p>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Sparkles className="h-4 w-4 flex-shrink-0" />
          <span>Sorted by popularity</span>
        </div>
      </div>

      {/* Templates Grid */}
      <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'space-y-4'} gap-6`}>
        {filteredTemplates.map((template) => (
          <Card key={template.id} className={`hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${viewMode === 'list' ? 'flex items-start gap-4 p-4' : ''}`}>
            <div className={viewMode === 'list' ? 'flex-1 min-w-0' : 'p-6'}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="text-3xl flex-shrink-0">{template.icon}</div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{template.name}</h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${getPopularityColor(template.popularity)}`}>
                        {template.popularity === 'high' ? 'Popular' : 
                         template.popularity === 'medium' ? 'Moderate' : 'Low Use'}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded flex-shrink-0">
                        {template.category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center text-yellow-500 flex-shrink-0">
                  <Star className="h-4 w-4 fill-current flex-shrink-0" />
                  <span className="ml-1 text-sm font-medium">{template.rating}</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.description}</p>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-4">
                {template.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg flex-shrink-0"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              {/* Stats - Fixed alignment */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-900">{template.fields}</p>
                  <p className="text-xs text-gray-500">Fields</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-900">{template.usageCount}</p>
                  <p className="text-xs text-gray-500">Used</p>
                </div>
                <div className="text-center">
                  <div className="min-h-[40px] flex flex-col items-center justify-center">
                    <p className="text-lg font-bold text-gray-900 mb-1">
                      {template.lastUsed.split(' ')[0]}
                    </p>
                    <p className="text-xs text-gray-500 font-normal">
                      {template.lastUsed.split(' ').slice(1).join(' ')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions - Fixed button alignment */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="small"
                  fullWidth={viewMode === 'list'}
                  onClick={() => navigate(`/seller/sales/add-product?template=${template.id}`)}
                  className="flex-1 flex items-center justify-center min-h-[36px]"
                >
                  <Copy className="h-4 w-4 mr-2 flex-shrink-0" />
                  Use Template
                </Button>
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => alert(`Previewing ${template.name}`)}
                    title="Preview"
                    className="flex items-center justify-center min-h-[36px] min-w-[36px]"
                  >
                    <Eye className="h-4 w-4 flex-shrink-0" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => alert(`Downloading ${template.name}`)}
                    title="Download"
                    className="flex items-center justify-center min-h-[36px] min-w-[36px]"
                  >
                    <Download className="h-4 w-4 flex-shrink-0" />
                  </Button>
                  {template.createdBy === 'You' && (
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => alert(`Editing ${template.name}`)}
                      title="Edit"
                      className="flex items-center justify-center min-h-[36px] min-w-[36px]"
                    >
                      <Settings className="h-4 w-4 flex-shrink-0" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredTemplates.length === 0 && (
        <Card className="text-center p-12">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <FileText className="h-10 w-10 text-gray-400 flex-shrink-0" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600 mb-6">
            {searchQuery 
              ? `No templates match "${searchQuery}". Try different keywords.`
              : `No templates available in the "${categories.find(c => c.id === selectedCategory)?.name}" category.`
            }
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="flex items-center justify-center min-h-[36px]"
            >
              Clear Filters
            </Button>
            <Button
              onClick={() => navigate('/seller/sales/add-product')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 flex items-center justify-center min-h-[36px]"
            >
              <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
              Create Product Manually
            </Button>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-xl mr-4 flex-shrink-0">
              <BookOpen className="h-6 w-6 text-blue-600 flex-shrink-0" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 truncate">Template Guide</h4>
              <p className="text-sm text-gray-600 truncate">Learn best practices</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            fullWidth 
            className="flex items-center justify-center min-h-[36px]"
          >
            View Documentation
          </Button>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl mr-4 flex-shrink-0">
              <TrendingUp className="h-6 w-6 text-green-600 flex-shrink-0" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 truncate">Import Templates</h4>
              <p className="text-sm text-gray-600 truncate">Import from other platforms</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            fullWidth 
            className="flex items-center justify-center min-h-[36px]"
          >
            Import Now
          </Button>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl mr-4 flex-shrink-0">
              <Shield className="h-6 w-6 text-purple-600 flex-shrink-0" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 truncate">Template Analytics</h4>
              <p className="text-sm text-gray-600 truncate">Usage reports & insights</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            fullWidth 
            className="flex items-center justify-center min-h-[36px]"
          >
            View Analytics
          </Button>
        </Card>
      </div>

      {/* Pricing Information Card */}
      <Card className="mt-6">
        <div className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600 flex-shrink-0" />
            Pricing Information (INR)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm font-medium text-blue-900">Free Templates</p>
              <p className="text-2xl font-bold text-blue-700">{formatINR(0)}</p>
              <p className="text-xs text-blue-600">All basic templates included</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-sm font-medium text-purple-900">Premium Templates</p>
              <p className="text-2xl font-bold text-purple-700">{formatINR(999)}</p>
              <p className="text-xs text-purple-600">Advanced industry templates</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <p className="text-sm font-medium text-green-900">Custom Templates</p>
              <p className="text-2xl font-bold text-green-700">{formatINR(2499)}</p>
              <p className="text-xs text-green-600">Tailored to your business</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProductTemplates;